<template>
  <div class="form-group">
    <label class="form-label"
           :class="labelclass"
           :data-content="label">
      <span class="">{{ label }}</span>
    </label>
    <div class="mention-textarea-container">
      <div contenteditable
           ref="editableContent"
           style="white-space: pre-wrap"
           class="form-control form-control-white"
           @input="handleInput"
           v-html="modelValue"></div>
      <div v-if="showMentions"
           class="mention-dropdown"
           :style="dropdownStyle">
        <div v-for="(user, index) in filteredUsers"
             :key="user.id"
             :class="['mention-item', { active: selectedMentionIndex === index }]"
             @click="selectMention(user)">
          {{ user.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, nextTick } from 'vue'
import { useStore } from 'vuex'

defineProps({
  modelValue: String,
  labelclass: String,
  label: String
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()

const editableContent = ref(null)
const showMentions = ref(false)
const filteredUsers = ref([])
const selectedMentionIndex = ref(0)
const mentionStartPos = ref(0)
const mentionQuery = ref('')
const dropdownStyle = ref({})

const getStaffUsers = computed(() => store.getters.getStaffUsers)

const calculateDropdownPosition = () => {
  nextTick(() => {
    const textarea = editableContent.value
    if (textarea) {
      const rect = textarea.getBoundingClientRect()
      dropdownStyle.value = {
        position: 'absolute',
        top: `${rect.height + 0}px`,
        left: '0px',
        zIndex: 1000
      }
    }
  })
}

const handleInput = () => {

  const el = editableContent.value
  const htmlValue = el.innerHTML
  const strippedValue = el.innerText // same coordinate system as getPlainCaretIndex
  const saved = saveSelection(el)
  const numOfLines = countLineBreaksBeforeCaret(el, saved) // starts with zero
  const caretIndex = getPlainCaretIndex(el) + numOfLines // plain-text caret index that matches strippedValue
  const lastAtSymbol = strippedValue.lastIndexOf('@', caretIndex - 1) + numOfLines

  const atSymbolFound = strippedValue.lastIndexOf('@', caretIndex - 1) !== -1
  const startSearchForUser = atSymbolFound

  // console.log(`caret index ${caretIndex}`, `@ is at index: ${lastAtSymbol}`, `symbol before @ symbol ${strippedValue[lastAtSymbol - 1]}`, `len of whole text ${strippedValue.length}`, `number of lines ${numOfLines}`)

  if (startSearchForUser) {
    const query = strippedValue.substring(lastAtSymbol + 1, caretIndex)
    if (!query.includes(' ')) {
      mentionStartPos.value = lastAtSymbol - numOfLines // plain-text index
      mentionQuery.value = query
      filteredUsers.value = getStaffUsers.value.filter(user =>
          user.name.toLowerCase().includes(query.toLowerCase())
      );
      selectedMentionIndex.value = 0
      showMentions.value = filteredUsers.value.length > 0
      calculateDropdownPosition()
    } else {
      showMentions.value = false
    }
  } else {
    showMentions.value = false
  }

  emit('update:modelValue', htmlValue)

  nextTick(() => restoreSelection(el, saved))
};

const selectMention = async (user) => {
  const el = editableContent.value;

  const startIndex = mentionStartPos.value; // plain-text
  const endIndex = mentionStartPos.value + mentionQuery.value.length + 1; // caret plain-text index now, we add one because of @ sign

  // build the span node to insert
  const span = document.createElement('span')
  span.className = 'user-tag'
  span.setAttribute('data-id', String(user.id))
  span.textContent = `@${user.name}`

  // insert a trailing whitespace node
  const nbsp = document.createTextNode('\u00A0')

  replaceTextRangeWithNode(el, startIndex, endIndex, span)
  // after replace, insert nbsp after inserted span
  if (span.nextSibling) {
    span.parentNode.insertBefore(nbsp, span.nextSibling)
  } else {
    span.parentNode.appendChild(nbsp)
  }

  removeLooseAtSymbols(el)
  // unwrapLastDiv(el)

  showMentions.value = false

  // set caret after inserted mention
  await nextTick()
  const range = document.createRange()
  const sel = window.getSelection()
  const afterNode = nbsp

  range.setStart(afterNode, 1)
  range.collapse(true)

  sel.removeAllRanges()
  sel.addRange(range)
  el.focus()

  await emitChange()
};

const emitChange = async () => {
  const el = editableContent.value
  // emit('update:modelValue', el.innerHTML)
  emit('update:modelValue', replaceDivWithBr(el.innerHTML))
  const saved = saveSelection(el)
  await nextTick()
  restoreSelection(el, saved)
}

// // --- helpers ---
function _getPath(node, root) {
  const path = [];
  while (node && node !== root) {
    const parent = node.parentNode;
    if (!parent) break;
    // find index among parent's childNodes
    let idx = 0;
    for (; idx < parent.childNodes.length; idx++) {
      if (parent.childNodes[idx] === node) break;
    }
    path.unshift(idx);
    node = parent;
  }
  return path;
}

function _getNodeByPath(root, path) {
  let node = root;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (!node || !node.childNodes || idx >= node.childNodes.length) return null;
    node = node.childNodes[idx];
  }
  return node;
}

// // --- save selection ---
function saveSelection(root) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  return {
    collapsed: sel.isCollapsed,
    startPath: _getPath(range.startContainer, root),
    startOffset: range.startOffset,
    endPath: _getPath(range.endContainer, root),
    endOffset: range.endOffset
  };
}

// // --- restore selection ---
function restoreSelection(root, saved) {
  if (!saved) return false

  const range = document.createRange()

  // find nodes from saved paths
  const startNodeResolved = _getNodeByPath(root, saved.startPath)
  const endNodeResolved = _getNodeByPath(root, saved.endPath)

  // If path resolution failed, place cursor at end instead of beginning
  if (!startNodeResolved || !endNodeResolved) {
    range.selectNodeContents(root)
    range.collapse(false)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
    return true
  }

  const startNode = startNodeResolved
  const endNode = endNodeResolved

  // clamp offsets to node capacity (text node length or child count)
  const clampOffset = (node, offset) => {
    if (!node) return 0
    if (node.nodeType === Node.TEXT_NODE) return Math.min(offset, node.nodeValue.length)
    // element node -> offset is between children, so max is childNodes.length
    return Math.min(offset, node.childNodes ? node.childNodes.length : 0)
  };

  const sOffset = clampOffset(startNode, saved.startOffset)
  const eOffset = clampOffset(endNode, saved.endOffset)

  try {
    range.setStart(startNode, sOffset)
    range.setEnd(endNode, eOffset)
  } catch (err) {
    // if setStart/setEnd fails, fallback: place caret at end
    range.selectNodeContents(root)
    range.collapse(false)
  }

  const sel = window.getSelection()
  sel.removeAllRanges()
  sel.addRange(range)
  return true
}


// --- helpers: compute caret index and map plain-text offset -> text node+offset ---

/**
 * Return caret index (number of characters) inside root based on the current selection.
 * Works by making a range from start of root to selection start and measuring its text.
 * This keeps the index in the same coordinate system as el.innerText.
 */
function getPlainCaretIndex(root) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return 0
  const range = sel.getRangeAt(0)

  // clone and limit to root → caret start
  const pre = range.cloneRange()
  pre.selectNodeContents(root)
  pre.setEnd(range.startContainer, range.startOffset)

  // Normalize returned length to match innerText as closely as possible
  return pre.toString().length
}

/**
 * Find the text node and offset inside that node that corresponds to `index`
 * measured in plain-text characters (same coordinate system as root.innerText).
 * Returns { node, offset } or null if out-of-range.
 */
function getTextNodeAt(root, index) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let node
  let current = 0
  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length
    if (current + len >= index) {
      return { node, offset: index - current }
    }
    current += len
  }

  // If index equals total length, return last text node's end (or root)
  if (index === current) {
    // find last text node
    let last = null
    const walker2 = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
    while ((node = walker2.nextNode())) last = node
    if (last) return { node: last, offset: last.nodeValue.length }
  }

  return null
}

/**
 * Replace the text between startIndex and endIndex (plain-text coordinates) with `node`.
 * `node` is an actual DOM Node (e.g. a span).
 */
function replaceTextRangeWithNode(root, startIndex, endIndex, node) {
  const start = getTextNodeAt(root, startIndex)
  const end = getTextNodeAt(root, endIndex)
  // console.log(start, end);
  // console.log(startIndex, endIndex);

  // if (!start) {
  if (!start || !end) {
    // fallback: append and place caret at end
    root.appendChild(node)
    return;
  }

  const range = document.createRange()
  try {
    range.setStart(start.node, start.offset)
    range.setEnd(end.node, end.offset)
    // range.setEnd(start.node, start.offset + (endIndex - startIndex));
  } catch (err) {
    console.log(err)
    // if anything fails, put node at end
    root.appendChild(node)
    return;
  }

  range.deleteContents()
  range.insertNode(node)

  // Normalize: join adjacent text nodes to keep DOM tidy
  root.normalize()
}

function countLineBreaksBeforeCaret(root, caretInfo) {
  let count = 0;

  const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_ELEMENT,
      null
  );

  let node;
  while ((node = walker.nextNode())) {
    if (node === caretInfo.startContainer) break

    if (node.tagName === "BR") count++
    if (node.tagName === "DIV" || node.tagName === "P") count++
  }

  return count
}

function replaceDivWithBr(htmlString) {
  let result = htmlString.replace(/<div>/g, '<br>')

  result = result.replace(/<\/div>/g, '<br>')

  // For example, if you had "</div><div>" it would become "<br><br>". You might want just one <br>.
  result = result.replace(/<br>\s*<br>/g, '<br>')

  // Trim any leading or trailing <br> tags if desired
  result = result.replace(/^<br>|<br>$/g, '')

  return result
}

function removeLooseAtSymbols(root) {
  const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          // Skip text inside user-tag spans
          if (node.parentNode.closest('.user-tag')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
  );

  let node
  const toEdit = []

  while ((node = walker.nextNode())) {
    if (node.nodeValue.includes('@')) {
      toEdit.push(node) // store first to avoid modifying while walking
    }
  }

  // Replace only in allowed nodes
  for (const textNode of toEdit) {
    textNode.nodeValue = textNode.nodeValue.replace(/@+/g, '')
  }
}
</script>

<style scoped lang="scss">
.mention-textarea-container {
  position: relative;
}

.mention-dropdown {
  position: absolute;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  z-index: 1000;
}

.mention-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;

  &:hover,
  &.active {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
}
</style>
